/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
import React from 'react';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { LineChart, BarChart } from 'echarts/charts';
import { MarkLineComponent, TooltipComponent, GridComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useIsReducedMotion } from '@atlaskit/motion';
import { colors, fontSizeSmall } from '@atlaskit/theme';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { secondsToDays, secondsToHours } from '../../../common/types';
import {
	CHART_SPACE_LEFT,
	CHART_SPACE_RIGHT,
} from '../../../common/ui/historical-cycle-time/constants';
import { BAR_WIDTH } from './constants';
import messages from './messages';
import { useTooltipFormatter } from './tooltip';
import type { Props } from './types';

echarts.use([
	BarChart,
	LineChart,
	MarkLineComponent,
	TooltipComponent,
	GridComponent,
	CanvasRenderer,
]);

const Chart = ({ dataPoints, medianInSeconds, displayUnits, onEvents }: Props) => {
	const { formatMessage, formatDate } = useIntl();
	const tooltipFormatter = useTooltipFormatter(medianInSeconds);
	const prefersReducedMotion = useIsReducedMotion();
	const displayDataPoints =
		displayUnits === 'days'
			? dataPoints.map((dataPoint) => ({
					...dataPoint,
					value: secondsToDays(dataPoint.value),
				}))
			: dataPoints.map((dataPoint) => ({
					...dataPoint,
					value: secondsToHours(dataPoint.value),
				}));

	const axisCommonStyle = {
		nameGap: gridSize * 4,
		nameLocation: 'middle',
		nameTextStyle: {
			color: colors.N400,
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: fontSizeSmall(),
		},
	};

	// Handle dark mode tokens for echarts in RLSS-2812
	const chartOptions = {
		animation: !prefersReducedMotion,
		tooltip: {
			backgroundColor: colors.N900,
			borderWidth: 0,
			formatter: tooltipFormatter,
			confine: true,
		},
		itemStyle: {
			borderRadius: [2, 2, 0, 0],
		},
		grid: {
			left: CHART_SPACE_LEFT,
			right: CHART_SPACE_RIGHT,
			top: gridSize * 2,
			bottom: gridSize * 8,
		},
		xAxis: {
			data: displayDataPoints.map((data) => {
				const { startDateInclusive } = data.customInfo;
				return data.customInfo.emphasisColor !== null
					? {
							value: formatDate(startDateInclusive, {
								month: 'short',
								day: 'numeric',
							}),
							textStyle: {
								color: data.customInfo.emphasisColor,
							},
						}
					: formatDate(startDateInclusive, {
							month: 'short',
							day: 'numeric',
						});
			}),
			axisLine: {
				lineStyle: {
					color: colors.N40,
					width: 2,
				},
			},
			axisTick: {
				alignWithLabel: true,
			},
			offset: gridSize * 2,
			...axisCommonStyle,
		},
		yAxis: {
			name:
				displayUnits === 'days'
					? formatMessage(messages.yAxisLabelDays)
					: formatMessage(messages.yAxisLabelHours),
			...axisCommonStyle,
			axisLine: {
				show: false,
			},
			splitNumber: 4,
			axisTick: false,
			splitLine: {
				lineStyle: {
					color: colors.N30,
				},
			},
		},
		textStyle: {
			color: colors.N400,
		},
		backgroundColor: 'transparent',
		series: [
			{
				type: 'bar',
				data: displayDataPoints,
				color: colors.B100,
				barWidth: BAR_WIDTH,
				...(medianInSeconds === null
					? {}
					: {
							markLine: {
								data: [
									{
										yAxis:
											displayUnits === 'days'
												? secondsToDays(medianInSeconds)
												: secondsToHours(medianInSeconds),
									},
								],
								silent: true,
								lineStyle: {
									color: colors.N300,
									width: 1,
								},
								symbol: 'none',
								label: { show: false },
							},
						}),
			},
		],
	};

	return (
		<ReactEchartsCore
			echarts={echarts}
			option={chartOptions}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ height: '100%' }}
			onEvents={onEvents}
		/>
	);
};

export default Chart;
